/**
 * SchemaDeployDialog — Modal for deploying converted schema to target database.
 *
 * Shows: target connection info, schema name input, overwrite toggle,
 * object checkboxes (category + individual), progress bar, per-statement results.
 */

import { useState } from 'react'
import {
  X, Play, Check, XCircle, AlertTriangle,
  Loader2, Table2, FileCode, Hash, ChevronDown, ChevronRight, RotateCcw, Code
} from 'lucide-react'
import { redeployFailed } from '../../data/schemaDeployer'

function SchemaDeployDialog({
  isOpen,
  onClose,
  convertedSchema,
  connection,
  targetDialect,
  onDeploy,
  checkedObjects = new Set(),
}) {
  const [schemaName, setSchemaName] = useState('')
  const [overwrite, setOverwrite] = useState(false)
  const [deployTables, setDeployTables] = useState(true)
  const [deployProcedures, setDeployProcedures] = useState(true)
  const [deployIndexes, setDeployIndexes] = useState(true)
  const [deploying, setDeploying] = useState(false)
  const [progress, setProgress] = useState(null)
  const [results, setResults] = useState(null)
  const [logs, setLogs] = useState([])
  const [showObjectList, setShowObjectList] = useState(false)
  const [expandedLogSql, setExpandedLogSql] = useState({})
  const [retrying, setRetrying] = useState(false)

  if (!isOpen) return null

  // Filter objects by checked state
  const checkedTables = convertedSchema?.tables.filter(t => checkedObjects.has(`table:${t.name}`)) || []
  const checkedProcs = convertedSchema?.procedures.filter(p => checkedObjects.has(`procedure:${p.name}`)) || []
  const checkedIndexes = convertedSchema?.indexes.filter(i => checkedObjects.has(`index:${i.name}`)) || []

  const totalChecked = checkedTables.length + checkedProcs.length + checkedIndexes.length

  const handleDeploy = async () => {
    setDeploying(true)
    setLogs([])
    setResults(null)

    // Build a filtered schema with only checked+enabled objects
    const filteredSchema = {
      ...convertedSchema,
      tables: deployTables ? checkedTables : [],
      procedures: deployProcedures ? checkedProcs : [],
      indexes: deployIndexes ? checkedIndexes : [],
      views: [], // views not deployed yet
    }

    const deployResult = await onDeploy({
      schemaName,
      overwrite,
      deployTables,
      deployProcedures,
      deployIndexes,
      filteredSchema,
    }, (progressEvent) => {
      setProgress(progressEvent)
      setLogs(prev => [...prev, progressEvent])
    })

    setResults(deployResult)
    setDeploying(false)
  }

  const connName = connection?.name || connection?.connectionFields?.host || 'Unknown'

  return (
    <div className="schema-deploy-overlay">
      <div className="schema-deploy-dialog">
        {/* Header */}
        <div className="schema-deploy-header">
          <h3>Deploy Schema</h3>
          <button className="schema-deploy-close" onClick={onClose} disabled={deploying}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="schema-deploy-body">
          {/* Connection info */}
          <div className="schema-deploy-section">
            <div className="schema-deploy-label">Target Connection</div>
            <div className="schema-deploy-value">{connName} ({targetDialect})</div>
          </div>

          {/* Schema name */}
          <div className="schema-deploy-section">
            <div className="schema-deploy-label">Schema Name (optional)</div>
            <input
              className="schema-deploy-input"
              value={schemaName}
              onChange={e => setSchemaName(e.target.value)}
              placeholder="e.g. tims_demo"
              disabled={deploying}
            />
          </div>

          {/* Options */}
          <div className="schema-deploy-section">
            <label className="schema-deploy-checkbox">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={e => setOverwrite(e.target.checked)}
                disabled={deploying}
              />
              <span>Overwrite (DROP existing tables first)</span>
            </label>
          </div>

          {/* Object selection — category level */}
          <div className="schema-deploy-section">
            <div className="schema-deploy-label">Objects to Deploy ({totalChecked} selected)</div>
            <label className="schema-deploy-checkbox">
              <input
                type="checkbox"
                checked={deployTables}
                onChange={e => setDeployTables(e.target.checked)}
                disabled={deploying}
              />
              <Table2 size={13} />
              <span>Tables ({checkedTables.length})</span>
            </label>
            <label className="schema-deploy-checkbox">
              <input
                type="checkbox"
                checked={deployProcedures}
                onChange={e => setDeployProcedures(e.target.checked)}
                disabled={deploying}
              />
              <FileCode size={13} />
              <span>Procedures ({checkedProcs.length})</span>
            </label>
            <label className="schema-deploy-checkbox">
              <input
                type="checkbox"
                checked={deployIndexes}
                onChange={e => setDeployIndexes(e.target.checked)}
                disabled={deploying}
              />
              <Hash size={13} />
              <span>Indexes ({checkedIndexes.length})</span>
            </label>
          </div>

          {/* Expandable individual object list */}
          <div className="schema-deploy-section">
            <button
              className="schema-deploy-toggle-list"
              onClick={() => setShowObjectList(v => !v)}
            >
              {showObjectList ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span>View individual objects</span>
            </button>
            {showObjectList && (
              <div className="schema-deploy-object-list">
                {deployTables && checkedTables.length > 0 && (
                  <>
                    <div className="schema-deploy-object-group">Tables</div>
                    {checkedTables.map(t => (
                      <div key={t.name} className="schema-deploy-object-item">
                        <Table2 size={11} />
                        <span>{t.name}</span>
                        <span className="schema-deploy-object-meta">{t.columns.length} cols</span>
                      </div>
                    ))}
                  </>
                )}
                {deployProcedures && checkedProcs.length > 0 && (
                  <>
                    <div className="schema-deploy-object-group">Procedures</div>
                    {checkedProcs.map(p => (
                      <div key={p.name} className="schema-deploy-object-item">
                        <FileCode size={11} />
                        <span>{p.name}</span>
                      </div>
                    ))}
                  </>
                )}
                {deployIndexes && checkedIndexes.length > 0 && (
                  <>
                    <div className="schema-deploy-object-group">Indexes</div>
                    {checkedIndexes.map(i => (
                      <div key={i.name} className="schema-deploy-object-item">
                        <Hash size={11} />
                        <span>{i.name}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Progress */}
          {deploying && progress && (
            <div className="schema-deploy-progress">
              <div className="schema-deploy-progress-bar">
                <div
                  className="schema-deploy-progress-fill"
                  style={{ width: `${progress.pct}%` }}
                />
              </div>
              <div className="schema-deploy-progress-label">
                <Loader2 size={13} className="schema-spinner" />
                {progress.label} ({progress.index + 1} / {progress.total})
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="schema-deploy-results">
              <div className={`schema-deploy-results-summary ${results.success ? 'success' : 'failure'}`}>
                {results.success ? (
                  <><Check size={16} /> Deployment complete</>
                ) : (
                  <><AlertTriangle size={16} /> Deployment completed with errors</>
                )}
              </div>
              <div className="schema-deploy-results-stats">
                <span className="schema-deploy-stat-ok">{results.succeeded} succeeded</span>
                {results.failed > 0 && <span className="schema-deploy-stat-fail">{results.failed} failed</span>}
                {results.skipped > 0 && <span className="schema-deploy-stat-skip">{results.skipped} skipped</span>}
              </div>
            </div>
          )}

          {/* Verification summary */}
          {results && results.results && (
            <div className="schema-deploy-verification">
              <div className="schema-deploy-label">Verification Summary</div>
              <div className="schema-deploy-verification-table">
                <div className="schema-deploy-verification-header">
                  <span className="schema-verify-col-name">Object</span>
                  <span className="schema-verify-col-status">Status</span>
                  <span className="schema-verify-col-detail">Detail</span>
                </div>
                {results.results.map((r, i) => (
                  <div key={i} className={`schema-deploy-verification-row ${r.success ? 'ok' : r.optional ? 'skip' : 'fail'}`}>
                    <span className="schema-verify-col-name">{r.label}</span>
                    <span className="schema-verify-col-status">
                      {r.success ? (
                        <Check size={11} className="schema-status-ok" />
                      ) : r.optional ? (
                        <AlertTriangle size={11} className="schema-status-warning" />
                      ) : (
                        <XCircle size={11} className="schema-status-error" />
                      )}
                      {r.success ? 'OK' : r.optional ? 'Skipped' : 'Failed'}
                    </span>
                    <span className="schema-verify-col-detail">
                      {r.error || (r.success ? 'Executed successfully' : '')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Execution log with collapsible SQL */}
          {logs.length > 0 && (
            <div className="schema-deploy-log">
              <div className="schema-deploy-label">Execution Log</div>
              <div className="schema-deploy-log-list">
                {logs.map((log, i) => (
                  <div key={i}>
                    <div className={`schema-deploy-log-item ${log.success ? '' : log.error ? 'error' : ''}`}>
                      {log.success ? (
                        <Check size={11} className="schema-status-ok" />
                      ) : (
                        <XCircle size={11} className="schema-status-error" />
                      )}
                      <span className="schema-deploy-log-label">{log.label}</span>
                      {log.sql && (
                        <button
                          className="schema-deploy-log-sql-toggle"
                          onClick={() => setExpandedLogSql(prev => ({ ...prev, [i]: !prev[i] }))}
                          title="Show SQL"
                        >
                          <Code size={10} />
                        </button>
                      )}
                      {log.error && <span className="schema-deploy-log-error">{log.error}</span>}
                    </div>
                    {expandedLogSql[i] && log.sql && (
                      <pre className="schema-deploy-log-sql">{log.sql}</pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="schema-deploy-footer">
          <button
            className="schema-deploy-btn-cancel"
            onClick={onClose}
            disabled={deploying || retrying}
          >
            {results ? 'Close' : 'Cancel'}
          </button>
          {results && results.failed > 0 && (
            <button
              className="schema-deploy-btn-deploy"
              style={{ background: '#f59e0b' }}
              onClick={async () => {
                setRetrying(true)
                const failedStmts = results.results.filter(r => !r.success && !r.optional)
                setLogs([])
                setResults(null)
                setProgress(null)
                const retryResult = await redeployFailed(failedStmts, connection, (progressEvent) => {
                  setProgress(progressEvent)
                  setLogs(prev => [...prev, progressEvent])
                })
                setResults(retryResult)
                setRetrying(false)
              }}
              disabled={retrying}
            >
              {retrying ? (
                <><Loader2 size={14} className="schema-spinner" /> Retrying...</>
              ) : (
                <><RotateCcw size={14} /> Re-deploy Failed ({results.failed})</>
              )}
            </button>
          )}
          {!results && (
            <button
              className="schema-deploy-btn-deploy"
              onClick={handleDeploy}
              disabled={deploying || !convertedSchema || totalChecked === 0}
            >
              {deploying ? (
                <><Loader2 size={14} className="schema-spinner" /> Deploying...</>
              ) : (
                <><Play size={14} /> Deploy ({totalChecked})</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default SchemaDeployDialog
